import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Upload, ScanLine, Sparkles, X, Mail, MessageCircle, Loader, Wand2 } from 'lucide-react';
import {
  getEmailTemplates, getSmsTemplates,
  createEmailTemplate, updateEmailTemplate, activateEmailTemplate, deleteEmailTemplate,
  createSmsTemplate, updateSmsTemplate, deleteSmsTemplate,
  importCommunicationTemplate, askMiloAgent,
  getGuests, sendEmails, sendSms,
} from '../../../data/api';
import { titleizeStatus } from './ciUtils';
import { Loading, ComingWave, useToast } from './CiShared';
import RichEditor from './RichEditor';
import { sanitizeHtml } from '../../../utils/sanitizeHtml';

// Templates — Email + SMS + Internal Notes tabs. FULLY FUNCTIONAL via api.js:
//   • Lists       ← GET /api/email-templates · GET /api/sms-templates
//   • New / Edit  ← POST|PUT /api/email-templates · POST|PUT /api/sms-templates
//   • ⋯ menu      = Edit · Duplicate · Preview · Test send · Set default
//                   (PATCH …/activate, email only) · Archive (DELETE)
//   • Scan/Upload ← client reads an .html/.txt/.eml file, then
//                   POST /api/communications/templates/import (real)
//   • AI rewrite  ← POST /api/milo/agent (askMiloAgent); graceful "needs API
//                   key" state when the agent reports configured:false.
// System-protected rows (live confirmations) expose only Preview + Duplicate.
//
// Internal Notes are reusable team-only snippets. They have no send channel, so
// they reuse the email_templates collection with template_type:'note' &
// is_active:false — the SAME persistence convention CreateScreen / InboxScreen
// already use for the "internal note" channel. They are filtered OUT of the
// Email tab so the two lists never overlap. No subject, no Milo-eligibility, no
// Test-send, no Set-default for notes; everything else (create/edit/duplicate/
// preview/archive/Milo-draft) works against the real endpoints.

const TABS = [
  { key: 'email', label: 'Email Templates' },
  { key: 'sms', label: 'SMS Templates' },
  { key: 'notes', label: 'Internal Notes' },
];

const TONES = [
  { key: 'Warmer', instruction: 'warmer, friendlier and more personal in tone' },
  { key: 'Formal', instruction: 'more formal, polished and professional in tone' },
  { key: 'Luxury', instruction: 'more elevated, elegant and luxurious in tone, befitting a high-end celebration' },
  { key: 'Concise', instruction: 'more concise and to the point, removing any filler while keeping the meaning' },
];

function categoryClass(type) {
  const t = String(type || '').toLowerCase();
  return t.includes('market') || t.includes('save') ? 'ci-cat ci-mk' : 'ci-cat';
}

// ── tiny self-contained modal (no global modal primitive in this module) ──
function Modal({ title, onClose, children, footer, wide }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(6,7,10,0.66)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="ci-glass"
        style={{ width: '100%', maxWidth: wide ? 760 : 540, padding: 0, boxShadow: '0 30px 80px rgba(0,0,0,0.6)', maxHeight: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', margin: 'auto' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 18px', borderBottom: '1px solid var(--ci-bd)', flex: 'none' }}>
          <div style={{ fontWeight: 700, fontSize: 15, flex: 1 }}>{title}</div>
          <button className="ci-kebab" onClick={onClose} aria-label="Close" style={{ fontSize: 16 }}><X size={16} /></button>
        </div>
        <div style={{ padding: 18, overflowY: 'auto', flex: '1 1 auto' }}>{children}</div>
        {footer && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 18px', borderTop: '1px solid var(--ci-bd)', flex: 'none' }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// Normalize a raw email / sms / note doc to the editor's working shape.
function toForm(channel, t) {
  if (channel === 'sms') {
    return {
      name: t?.name || '',
      template_type: t?.template_type || 'custom',
      message_text: t?.message_text || '',
    };
  }
  if (channel === 'notes') {
    // Notes ride on email_templates (template_type:'note'); internal-only, so
    // no subject and no Milo-eligibility, but they carry formatted HTML.
    return {
      name: t?.name || '',
      template_type: 'note',
      html_content: t?.html_content || '',
    };
  }
  return {
    name: t?.name || '',
    subject: t?.subject || '',
    template_type: t?.template_type || 'custom',
    html_content: t?.html_content || '',
    is_milo_eligible: !!t?.is_milo_eligible,
  };
}

export default function TemplatesScreen() {
  const [tab, setTab] = useState('email');
  const [emailTpls, setEmailTpls] = useState(null);
  const [smsTpls, setSmsTpls] = useState(null);
  const [err, setErr] = useState(false);
  const [openMenu, setOpenMenu] = useState('');
  const { show, node: toastNode } = useToast();

  // Editor modal: { mode:'new'|'edit', channel, id?, form }
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);

  // Preview modal: { channel, name, subject, html, text }
  const [preview, setPreview] = useState(null);

  // Test-send modal: { channel, id, name }
  const [testSend, setTestSend] = useState(null);

  // AI rewrite modal: { channel, id, name, original, tone, status, result, configured }
  const [rewrite, setRewrite] = useState(null);

  const fileRef = useRef(null);
  const importChannelRef = useRef('email');

  const reloadEmail = useCallback(() => {
    return getEmailTemplates()
      .then((d) => { setEmailTpls(Array.isArray(d) ? d : []); setErr(false); })
      .catch(() => { setEmailTpls([]); setErr(true); });
  }, []);
  const reloadSms = useCallback(() => {
    return getSmsTemplates()
      .then((d) => { setSmsTpls(Array.isArray(d) ? d : []); })
      .catch(() => { setSmsTpls([]); });
  }, []);

  useEffect(() => {
    let alive = true;
    getEmailTemplates().then((d) => { if (alive) { setEmailTpls(Array.isArray(d) ? d : []); } }).catch(() => { if (alive) { setEmailTpls([]); setErr(true); } });
    getSmsTemplates().then((d) => { if (alive) setSmsTpls(Array.isArray(d) ? d : []); }).catch(() => { if (alive) setSmsTpls([]); });
    return () => { alive = false; };
  }, []);

  // Close any open kebab on outside click.
  useEffect(() => {
    if (!openMenu) return undefined;
    const close = () => setOpenMenu('');
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [openMenu]);

  // Notes share the email_templates collection (template_type:'note'); the
  // Notes tab therefore loads with the email list.
  const loading = (tab === 'email' && emailTpls === null)
    || (tab === 'notes' && emailTpls === null)
    || (tab === 'sms' && smsTpls === null);

  const isNoteTpl = (t) => String(t?.template_type || '').toLowerCase() === 'note';

  const rows = useMemo(() => {
    let rawList;
    if (tab === 'email') rawList = (emailTpls || []).filter((t) => !isNoteTpl(t));
    else if (tab === 'sms') rawList = (smsTpls || []);
    else if (tab === 'notes') rawList = (emailTpls || []).filter(isNoteTpl);
    else rawList = [];
    return rawList.map((t) => ({
    id: String(t.id || t._id || t.name),
    raw: t,
    name: t.name || 'Untitled template',
    sub: tab === 'sms'
      ? (t.message_text ? `${String(t.message_text).slice(0, 64)}${String(t.message_text).length > 64 ? '…' : ''}` : (titleizeStatus(t.template_type) || 'SMS template'))
      : tab === 'notes'
        ? (() => {
            const plain = String(t.html_content || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
            return plain ? `${plain.slice(0, 72)}${plain.length > 72 ? '…' : ''}` : 'Internal note';
          })()
        : (t.subject || titleizeStatus(t.template_type) || 'Email template'),
    type: t.template_type || 'custom',
    used: t.usage_count ?? t.times_used ?? null,
    isActive: !!t.is_active,
    canEdit: t.can_edit !== false && !t.is_builtin && !t.is_system_protected,
    canDelete: t.can_delete !== false && !t.is_builtin && !t.is_system_protected,
    protectedTpl: !!(t.is_builtin || t.is_system_protected),
    }));
  }, [emailTpls, smsTpls, tab]);

  // The active tab maps to an editor channel. 'notes' is its own channel even
  // though it persists through the email-template endpoints (template_type:'note').
  const channelForTab = () => (tab === 'sms' ? 'sms' : tab === 'notes' ? 'notes' : 'email');

  // ── New / Edit / Duplicate openers ──
  const openNew = () => {
    const channel = channelForTab();
    setEditor({ mode: 'new', channel, form: toForm(channel, null) });
  };
  const openEdit = (row) => {
    const channel = channelForTab();
    setEditor({ mode: 'edit', channel, id: row.id, form: toForm(channel, row.raw) });
  };
  const openDuplicate = (row) => {
    const channel = channelForTab();
    const form = toForm(channel, row.raw);
    form.name = `${form.name || 'Untitled template'} (copy)`;
    // A duplicate is always a brand-new, non-default draft.
    if (channel === 'email') form.is_milo_eligible = false;
    setEditor({ mode: 'new', channel, form });
  };

  const setField = (k, v) => setEditor((ed) => ({ ...ed, form: { ...ed.form, [k]: v } }));

  const saveEditor = async () => {
    if (!editor) return;
    const { mode, channel, id, form } = editor;
    if (!String(form.name || '').trim()) { show('Give the template a name first.'); return; }
    setSaving(true);
    try {
      if (channel === 'sms') {
        const payload = { name: form.name.trim(), template_type: form.template_type || 'custom', message_text: form.message_text || '' };
        if (mode === 'new') {
          if (!String(form.message_text || '').trim()) { show('Add the SMS message text first.'); setSaving(false); return; }
          await createSmsTemplate(payload);
        } else {
          await updateSmsTemplate(id, payload);
        }
        await reloadSms();
      } else if (channel === 'notes') {
        // Internal notes persist as inactive, note-typed email templates — the
        // same convention CreateScreen / InboxScreen use for the note channel.
        if (mode === 'new' && !String(form.html_content || '').replace(/<[^>]*>/g, '').trim()) {
          show('Add the note content first.'); setSaving(false); return;
        }
        const payload = {
          name: form.name.trim(),
          subject: '',
          template_type: 'note',
          html_content: form.html_content || '',
          is_active: false,
          is_milo_eligible: false,
        };
        if (mode === 'new') {
          await createEmailTemplate(payload);
        } else {
          await updateEmailTemplate(id, payload);
        }
        await reloadEmail();
      } else {
        const payload = {
          name: form.name.trim(),
          subject: form.subject || '',
          template_type: form.template_type || 'custom',
          html_content: form.html_content || '',
          is_milo_eligible: !!form.is_milo_eligible,
        };
        if (mode === 'new') {
          await createEmailTemplate(payload);
        } else {
          await updateEmailTemplate(id, payload);
        }
        await reloadEmail();
      }
      show(mode === 'new' ? 'Template created.' : 'Template saved.');
      setEditor(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not save the template.';
      show(typeof msg === 'string' ? msg : 'Could not save the template.');
    } finally {
      setSaving(false);
    }
  };

  // ── Preview (client-side, truthful to stored content) ──
  const openPreview = (row) => {
    const t = row.raw;
    if (tab === 'sms') {
      setPreview({ channel: 'sms', name: t.name || 'SMS template', text: t.message_text || '' });
    } else if (tab === 'notes') {
      setPreview({ channel: 'note', name: t.name || 'Internal note', html: t.html_content || '' });
    } else {
      setPreview({ channel: 'email', name: t.name || 'Email template', subject: t.subject || '', html: t.html_content || '' });
    }
  };

  // ── Set default (email only) ──
  const setDefault = async (row) => {
    try {
      await activateEmailTemplate(row.id);
      await reloadEmail();
      show(`"${row.name}" is now the default.`);
    } catch (e) {
      show(e?.response?.data?.detail || 'Could not set default.');
    }
  };

  // ── Archive (delete) ──
  const archive = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Archive "${row.name}"? This removes the template.`)) return;
    try {
      if (tab === 'sms') { await deleteSmsTemplate(row.id); await reloadSms(); }
      else { await deleteEmailTemplate(row.id); await reloadEmail(); }
      show('Template archived.');
    } catch (e) {
      show(e?.response?.data?.detail || 'Could not archive this template.');
    }
  };

  // ── Scan / Upload import (real) ──
  const triggerImport = (channel) => {
    importChannelRef.current = channel;
    if (fileRef.current) { fileRef.current.value = ''; fileRef.current.click(); }
  };
  const onFilePicked = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const name = file.name || 'Imported template';
    const lower = name.toLowerCase();
    if (!/\.(html?|txt|eml)$/.test(lower)) { show('Pick an .html, .txt, or .eml file.'); return; }
    try {
      const content = await file.text();
      const baseName = name.replace(/\.(html?|txt|eml)$/i, '').trim() || 'Imported template';
      const channel = tab === 'sms' ? 'sms' : 'email';
      const isHtml = /\.html?$/.test(lower) || /\.eml$/.test(lower);
      const payload = channel === 'sms'
        ? { name: baseName, type: 'sms', text: content, source: 'upload' }
        : { name: baseName, type: 'email', html: isHtml ? content : `<pre>${content.replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]))}</pre>`, source: 'upload' };
      await importCommunicationTemplate(payload);
      if (channel === 'sms') await reloadSms(); else await reloadEmail();
      show(`Imported "${baseName}" as a new ${channel.toUpperCase()} template.`);
    } catch (err2) {
      show(err2?.response?.data?.detail || 'Could not import that file.');
    }
  };

  // ── AI rewrite via Milo (real; graceful no-key state) ──
  const runRewrite = async (channel, row, tone) => {
    const t = row ? row.raw : null;
    const original = channel === 'sms'
      ? (t?.message_text || '')
      : (t?.html_content || t?.subject || '');
    setRewrite({ channel, id: row?.id || '', name: row?.name || 'this template', original, tone: tone.key, status: 'loading', result: '', configured: true });
    const kindWord = channel === 'sms' ? 'SMS' : channel === 'notes' ? 'internal note' : 'email';
    const prompt = `Rewrite the following ${kindWord} template to be ${tone.instruction}. Keep any {{merge_tags}} and links intact. Return only the rewritten ${channel === 'sms' ? 'message text' : 'content'}, with no preamble.\n\n--- TEMPLATE ---\n${original || '(empty)'}\n--- END ---`;
    try {
      const res = await askMiloAgent(prompt);
      const configured = res?.configured !== false;
      setRewrite((r) => r && {
        ...r,
        status: configured && res?.ok !== false ? 'done' : 'unconfigured',
        result: res?.answer || '',
        configured,
      });
    } catch (e) {
      setRewrite((r) => r && { ...r, status: 'error', result: e?.response?.data?.detail || 'Milo could not complete the rewrite.' });
    }
  };

  // Apply a rewrite result into the editor (so the admin can save it).
  const applyRewriteToEditor = () => {
    if (!rewrite || rewrite.status !== 'done') return;
    const row = rows.find((r) => r.id === rewrite.id);
    const channel = rewrite.channel;
    const form = toForm(channel, row ? row.raw : null);
    if (channel === 'sms') form.message_text = rewrite.result;
    else form.html_content = rewrite.result;
    setEditor({ mode: row ? 'edit' : 'new', channel, id: row ? row.id : undefined, form });
    setRewrite(null);
    show('Loaded Milo’s rewrite into the editor — review, then save.');
  };

  const handleMenu = (action, row) => {
    setOpenMenu('');
    if (action === 'edit') openEdit(row);
    else if (action === 'duplicate') openDuplicate(row);
    else if (action === 'preview') openPreview(row);
    // Notes are internal-only and have no send channel, so no Test-send.
    else if (action === 'test' && tab !== 'notes') setTestSend({ channel: tab === 'sms' ? 'sms' : 'email', id: row.id, name: row.name });
    else if (action === 'default') setDefault(row);
    else if (action === 'archive') archive(row);
    else if (action === 'rewrite') runRewrite(channelForTab(), row, TONES[0]);
  };

  return (
    <div className="ci-view">
      <input ref={fileRef} type="file" accept=".html,.htm,.txt,.eml" style={{ display: 'none' }} onChange={onFilePicked} />

      <div className="ci-ob-tabs">
        {TABS.map((t) => {
          // Surface a count per tab so a template saved to a non-default tab
          // (e.g. an SMS template while the Email tab is open) is discoverable.
          const count = t.key === 'email' ? (emailTpls || []).filter((x) => !isNoteTpl(x)).length
            : t.key === 'sms' ? (smsTpls || []).length
            : (emailTpls || []).filter(isNoteTpl).length;
          return (
            <button key={t.key} className={`ci-ob-tab ${tab === t.key ? 'ci-on' : ''}`} onClick={() => setTab(t.key)}>
              {t.label}{count ? ` (${count})` : ''}
            </button>
          );
        })}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button className="ci-btn ci-sm" disabled={tab === 'notes'} title={tab === 'notes' ? 'File import is for Email / SMS templates' : 'Import a template from a file'} onClick={() => triggerImport(tab === 'sms' ? 'sms' : 'email')}><ScanLine size={13} /> Scan</button>
          <button className="ci-btn ci-sm" disabled={tab === 'notes'} title={tab === 'notes' ? 'File import is for Email / SMS templates' : 'Upload an .html / .txt / .eml file'} onClick={() => triggerImport(tab === 'sms' ? 'sms' : 'email')}><Upload size={13} /> Upload</button>
          <button className="ci-btn ci-gold ci-sm" onClick={openNew}>
            <Plus size={13} /> {tab === 'notes' ? 'New Note' : 'New Template'}
          </button>
        </div>
      </div>

      {loading && <Loading label="Loading templates…" />}

      {!loading && tab === 'notes' && (
        <div className="ci-ts" style={{ margin: '2px 2px 12px' }}>
          Reusable internal-note snippets, visible to the event team only — never sent to a guest. Create one below, then reuse it from the Note channel when you draft a communication.
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="ci-glass" style={{ padding: 26 }}>
          <div className="ci-empty">{err && tab !== 'notes' ? 'Templates are unavailable right now.' : `No ${tab === 'email' ? 'email templates' : tab === 'sms' ? 'SMS templates' : 'internal notes'} yet. Create one to get started.`}</div>
        </div>
      )}

      {!loading && rows.map((t) => (
        <div className="ci-tpl-row" key={t.id}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ci-tn">
              {t.name}
              {t.isActive && <span className="ci-badge ci-active" style={{ marginLeft: 8 }}>Default</span>}
              {t.protectedTpl && <span className="ci-badge ci-sched" style={{ marginLeft: 8 }}>System</span>}
            </div>
            <div className="ci-ts">{t.sub}</div>
          </div>
          <span className={categoryClass(t.type)}>{titleizeStatus(t.type)}</span>
          <span className="ci-used">{t.used != null ? `Used ${t.used} times` : ''}</span>
          <div style={{ position: 'relative' }}>
            <button className="ci-kebab" onClick={(e) => { e.stopPropagation(); setOpenMenu(openMenu === t.id ? '' : t.id); }}>⋯</button>
            {openMenu === t.id && (
              <div className="ci-menu" onClick={(e) => e.stopPropagation()}>
                {t.canEdit && <button onClick={() => handleMenu('edit', t)}>{tab === 'notes' ? 'Edit note' : 'Edit template'}</button>}
                <button onClick={() => handleMenu('duplicate', t)}>Duplicate</button>
                <button onClick={() => handleMenu('preview', t)}>Preview</button>
                {tab !== 'notes' && <button onClick={() => handleMenu('test', t)}>Test send…</button>}
                {t.canEdit && <button onClick={() => handleMenu('rewrite', t)}>Rewrite with Milo</button>}
                {tab === 'email' && t.canEdit && !t.isActive && <button onClick={() => handleMenu('default', t)}>Set as default</button>}
                {t.canDelete && <button onClick={() => handleMenu('archive', t)} style={{ color: '#e9897f' }}>Archive</button>}
              </div>
            )}
          </div>
        </div>
      ))}

      {!loading && rows.length > 0 && (
        <div className="ci-ai-rw">
          <span style={{ fontSize: 12, color: 'var(--ci-txt3)', marginRight: 4 }}>Milo can rewrite the most-recent {tab === 'notes' ? 'note' : 'template'}:</span>
          {TONES.map((tone) => (
            <button
              key={tone.key}
              className="ci-btn ci-sm"
              title={`Rewrite "${(rows.find((r) => r.canEdit) || rows[0]).name}" to be ${tone.instruction}`}
              onClick={() => runRewrite(channelForTab(), rows.find((r) => r.canEdit) || rows[0], tone)}
            >
              <Sparkles size={12} /> {tone.key}
            </button>
          ))}
        </div>
      )}

      {/* ── Editor modal ── */}
      {editor && (
        <Modal
          title={`${editor.mode === 'new' ? 'New' : 'Edit'} ${editor.channel === 'sms' ? 'SMS template' : editor.channel === 'notes' ? 'internal note' : 'email template'}`}
          onClose={() => !saving && setEditor(null)}
          wide
          footer={(
            <>
              <button className="ci-btn ci-sm" disabled={saving} onClick={() => setEditor(null)}>Cancel</button>
              <button className="ci-btn ci-gold ci-sm" disabled={saving} onClick={saveEditor}>
                {saving ? <><Loader size={13} style={{ animation: 'ci-spin 0.8s linear infinite' }} /> Saving…</> : (editor.mode === 'new' ? (editor.channel === 'notes' ? 'Create note' : 'Create template') : 'Save changes')}
              </button>
            </>
          )}
        >
          <TemplateEditorBody key={`${editor.mode}-${editor.channel}-${editor.id || 'new'}`} editor={editor} setField={setField} />
        </Modal>
      )}

      {/* ── Preview modal ── */}
      {preview && (
        <Modal title={`Preview — ${preview.name}`} onClose={() => setPreview(null)} wide
          footer={<button className="ci-btn ci-sm" onClick={() => setPreview(null)}>Close</button>}>
          {preview.channel === 'email' && (
            <div>
              <div className="ci-email-doc">
                <div className="ci-email-hd"><div className="ci-email-subj">{preview.subject || '(no subject)'}</div></div>
                {preview.html
                  ? <div className="ci-email-body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(preview.html) }} />
                  : <div className="ci-email-body"><p style={{ color: 'var(--ci-txt3)' }}>This template has no content yet.</p></div>}
              </div>
            </div>
          )}
          {preview.channel === 'note' && (
            <div>
              <div style={{ fontSize: 11.5, color: 'var(--ci-txt3)', marginBottom: 8 }}>Internal note — visible to the event team only, never sent to a guest.</div>
              <div className="ci-glass" style={{ padding: 16 }}>
                {preview.html
                  ? <div className="ci-email-body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(preview.html) }} />
                  : <p style={{ color: 'var(--ci-txt3)', margin: 0 }}>This note has no content yet.</p>}
              </div>
            </div>
          )}
          {preview.channel === 'sms' && (
            <div>
              <div className="ci-phone" style={{ width: 300, margin: '0 auto' }}>
                <div className="ci-phone-notch" />
                <div className="ci-phone-hd"><div className="ci-pn">Event Host</div><div className="ci-ps">Text Message</div></div>
                <div className="ci-phone-body" style={{ minHeight: 160 }}>
                  <div className="ci-sms ci-in">{preview.text || 'This template has no message text yet.'}</div>
                </div>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* ── Test-send modal ── */}
      {testSend && (
        <TestSendModal info={testSend} onClose={() => setTestSend(null)} onSent={(m) => { show(m); setTestSend(null); }} />
      )}

      {/* ── AI rewrite modal ── */}
      {rewrite && (
        <Modal
          title={`Milo rewrite — ${rewrite.tone}`}
          onClose={() => setRewrite(null)}
          wide
          footer={(
            <>
              {rewrite.status === 'done' && <button className="ci-btn ci-sm" onClick={() => navigator.clipboard && navigator.clipboard.writeText(rewrite.result).then(() => show('Copied.'))}>Copy</button>}
              {rewrite.status === 'done' && <button className="ci-btn ci-gold ci-sm" onClick={applyRewriteToEditor}>Open in editor</button>}
              <button className="ci-btn ci-sm" onClick={() => setRewrite(null)}>Close</button>
            </>
          )}
        >
          <div style={{ fontSize: 12.5, color: 'var(--ci-txt2)', marginBottom: 10 }}>
            Rewriting <b style={{ color: 'var(--ci-txt)' }}>{rewrite.name}</b> to be {(TONES.find((x) => x.key === rewrite.tone) || {}).instruction || 'improved'}.
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            {TONES.map((tone) => (
              <button
                key={tone.key}
                className={`ci-btn ci-sm ${rewrite.tone === tone.key ? 'ci-gold' : ''}`}
                disabled={rewrite.status === 'loading'}
                onClick={() => runRewrite(rewrite.channel, rows.find((r) => r.id === rewrite.id), tone)}
              >
                {tone.key}
              </button>
            ))}
          </div>

          {rewrite.status === 'loading' && <Loading label="Milo is rewriting…" />}

          {rewrite.status === 'unconfigured' && (
            <ComingWave title="Milo’s AI rewrite needs an API key">
              {rewrite.result || 'Add an ANTHROPIC_API_KEY to the backend environment to turn on Milo’s tone rewrites. Until then, you can still create, edit, duplicate, import, preview and test-send templates — those all work without a key.'}
            </ComingWave>
          )}

          {rewrite.status === 'error' && (
            <div className="ci-glass" style={{ padding: 16 }}>
              <div className="ci-empty" style={{ color: '#e9897f' }}>{rewrite.result}</div>
            </div>
          )}

          {rewrite.status === 'done' && (
            <div>
              <div className="ci-lab" style={{ marginTop: 0 }}>Milo’s rewrite</div>
              <div className="ci-glass" style={{ padding: 14, whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.55, maxHeight: 320, overflowY: 'auto' }}>
                {rewrite.result || '(Milo returned an empty result — try another tone.)'}
              </div>
            </div>
          )}
        </Modal>
      )}

      {toastNode}
    </div>
  );
}

// Plain text → simple HTML paragraphs (folds a Milo plain-text draft into the
// rich editor cleanly).
function plainToHtml(text) {
  const esc = (s) => String(s).replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));
  const paras = String(text || '').split('\n').filter((l) => l.trim().length);
  return paras.length ? paras.map((p) => `<p>${esc(p)}</p>`).join('\n') : '';
}

// ── Editor body: name + category (+ subject for email), then a REAL rich editor
// (email & notes) or plain-text message (sms), plus a "Tell Milo to draft this"
// prompt that fills the subject + body. Self-contained Milo state so the parent
// stays lean. Notes are internal-only: rich body, no subject, no Milo-eligibility. ──
function TemplateEditorBody({ editor, setField }) {
  const isEmail = editor.channel === 'email';
  const isNote = editor.channel === 'notes';
  const usesHtml = isEmail || isNote; // both store an HTML body
  const kindWord = isEmail ? 'template' : isNote ? 'note' : 'template';
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [needsKey, setNeedsKey] = useState(false);

  const draftWithMilo = async () => {
    if (busy) return;
    const want = prompt.trim();
    if (!want) { setNote(`Tell Milo what this ${kindWord} should say first.`); return; }
    setBusy(true); setNote(''); setNeedsKey(false);
    // Email wants SUBJECT+BODY; SMS and notes are body-only.
    const fmt = isEmail
      ? 'Respond in EXACTLY this format and nothing else:\nSUBJECT: <one short subject line>\nBODY:\n<the message body>'
      : isNote
        ? 'Return only the note body, with no preamble.'
        : 'Return only the message text, with no preamble.';
    const subject = isEmail
      ? `Draft a warm, polished event email template for a high-end celebration. It should: "${want}". Use {{first_name}} where a personal greeting helps and {{rsvp_link}} where an RSVP call-to-action belongs. ${fmt}`
      : isNote
        // Notes are internal team reminders — no merge tags, no guest tone.
        ? `Draft a clear, concise internal team note for the event coordinators (never sent to a guest). It should: "${want}". ${fmt}`
        : `Draft a warm, polished event SMS template for a high-end celebration. It should: "${want}". Use {{first_name}} where a personal greeting helps and {{rsvp_link}} where an RSVP call-to-action belongs. ${fmt}`;
    try {
      const res = await askMiloAgent(subject);
      const configured = res?.configured !== false && res?.refusal_reason !== 'no_llm_key';
      if (!configured) { setNeedsKey(true); return; }
      const answer = String(res?.answer || '').trim();
      if (!answer) { setNote('Milo returned an empty draft — try rephrasing.'); return; }
      if (res?.refusal_reason) { setNote(answer); return; }
      if (isEmail) {
        let subj = '';
        let body = answer;
        const m = answer.match(/SUBJECT:\s*(.*?)\s*(?:\n+)BODY:\s*([\s\S]*)$/i);
        if (m) { subj = m[1].trim(); body = m[2].trim(); }
        if (subj) setField('subject', subj);
        setField('html_content', plainToHtml(body));
      } else if (isNote) {
        setField('html_content', plainToHtml(answer));
      } else {
        setField('message_text', answer);
      }
      setNote(`Milo drafted this — review and edit before saving.`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) setNeedsKey(true);
      else setNote(e?.response?.data?.detail || 'Milo is unavailable right now.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="ci-lab" style={{ marginTop: 0 }}>{isNote ? 'Note name' : 'Template name'}</div>
      <input className="ci-inp" value={editor.form.name} onChange={(e) => setField('name', e.target.value)} placeholder={isNote ? 'e.g. Day-of vendor checklist' : 'e.g. Reminder — Wave 1'} />

      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div className="ci-lab">Category</div>
          <input className="ci-inp" value={editor.form.template_type} onChange={(e) => setField('template_type', e.target.value)} placeholder={isNote ? 'note' : 'custom'} disabled={isNote} title={isNote ? 'Internal notes are always categorized as "note".' : undefined} />
        </div>
        {isEmail && (
          <div style={{ flex: 2 }}>
            <div className="ci-lab">Subject</div>
            <input className="ci-inp" value={editor.form.subject} onChange={(e) => setField('subject', e.target.value)} placeholder="Subject line" />
          </div>
        )}
      </div>

      {/* Tell Milo to draft this */}
      <div className="ci-lab">Tell Milo to draft this {isNote ? 'note' : ''}</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <input
          className="ci-inp"
          style={{ flex: 1, minWidth: 200 }}
          value={prompt}
          onChange={(e) => { setPrompt(e.target.value); setNote(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); draftWithMilo(); } }}
          placeholder={isEmail ? 'e.g. A formal RSVP reminder for a black-tie gala' : isNote ? 'e.g. A checklist of day-of vendor arrival times' : 'e.g. A short RSVP reminder text'}
        />
        <button className="ci-btn ci-gold ci-sm" disabled={busy} onClick={draftWithMilo} style={{ flex: 'none' }}>
          {busy ? <><Loader size={13} style={{ animation: 'ci-spin 0.8s linear infinite' }} /> Drafting…</> : <><Wand2 size={13} /> Draft</>}
        </button>
      </div>
      {note && <div style={{ fontSize: 11, color: 'var(--ci-txt2)', marginTop: 6 }}>{note}</div>}
      {needsKey && (
        <ComingWave title="Milo’s drafting needs an API key" tag="Action needed">
          Add a language-model key to the backend environment to let Milo draft {isNote ? 'notes' : 'templates'} for you. You can still write and format the {isNote ? 'note' : 'template'} yourself below — that works without a key.
        </ComingWave>
      )}

      {usesHtml ? (
        <>
          <div className="ci-lab">Content</div>
          <RichEditor
            value={editor.form.html_content}
            onChange={(html) => setField('html_content', html)}
            placeholder={isNote
              ? 'Write your internal note… use the toolbar to format. This is for the event team only — it is never sent to a guest.'
              : 'Write your email… use the toolbar to format, or insert {{first_name}} / {{rsvp_link}} merge tags.'}
            minHeight={200}
            headerFooter={isEmail}
          />
          {isEmail ? (
            <>
              <div className="ci-ts" style={{ marginTop: 6 }}>
                Body is saved as HTML. Use <code>{'{{first_name}}'}</code>, <code>{'{{guest_name}}'}</code>, <code>{'{{event_name}}'}</code> and <code>{'{{rsvp_link}}'}</code> merge tags.
              </div>
              <label className="ci-chk" style={{ marginTop: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!editor.form.is_milo_eligible} onChange={(e) => setField('is_milo_eligible', e.target.checked)} />
                Let Milo use this template for prefilled suggestions
              </label>
            </>
          ) : (
            <div className="ci-ts" style={{ marginTop: 6 }}>
              Internal note — visible to the event team only. It is saved as a reusable snippet and is never sent to a guest.
            </div>
          )}
        </>
      ) : (
        <>
          <div className="ci-lab">Message text</div>
          <textarea className="ci-inp" style={{ minHeight: 140 }} value={editor.form.message_text} onChange={(e) => setField('message_text', e.target.value)} placeholder="Your SMS message… Use {{first_name}} merge tags." />
          <div className="ci-ts" style={{ marginTop: 6 }}>
            {(editor.form.message_text || '').length} chars · {Math.max(1, Math.ceil(((editor.form.message_text || '').length || 1) / 160))} segment(s)
          </div>
        </>
      )}
    </>
  );
}

// ── Test send: pick one real guest, then send via the live send pipeline ──
function TestSendModal({ info, onClose, onSent }) {
  const [guests, setGuests] = useState(null);
  const [q, setQ] = useState('');
  const [chosen, setChosen] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    getGuests().then((d) => { if (alive) setGuests(Array.isArray(d) ? d : []); }).catch(() => { if (alive) setGuests([]); });
    return () => { alive = false; };
  }, []);

  const matches = useMemo(() => {
    const list = guests || [];
    const term = q.trim().toLowerCase();
    const need = info.channel === 'sms' ? (g) => !!g.phone : (g) => !!g.email;
    const filtered = list.filter(need).filter((g) => {
      if (!term) return true;
      return `${g.first_name} ${g.last_name} ${g.email} ${g.phone}`.toLowerCase().includes(term);
    });
    return filtered.slice(0, 8);
  }, [guests, q, info.channel]);

  const doSend = async () => {
    if (!chosen) { setError('Pick a recipient first.'); return; }
    setSending(true); setError('');
    try {
      if (info.channel === 'sms') {
        await sendSms(info.id, [chosen.id], { source: 'templates_test_send' });
      } else {
        await sendEmails(info.id, [chosen.id], [], [], false, { source: 'templates_test_send' });
      }
      const who = `${chosen.first_name || ''} ${chosen.last_name || ''}`.trim() || chosen.email || chosen.phone;
      onSent(`Test ${info.channel === 'sms' ? 'SMS' : 'email'} queued to ${who}.`);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not send the test. Check outbound settings.');
      setSending(false);
    }
  };

  return (
    <Modal
      title={`Test send — ${info.name}`}
      onClose={() => !sending && onClose()}
      footer={(
        <>
          <button className="ci-btn ci-sm" disabled={sending} onClick={onClose}>Cancel</button>
          <button className="ci-btn ci-gold ci-sm" disabled={sending || !chosen} onClick={doSend}>
            {sending ? <><Loader size={13} style={{ animation: 'ci-spin 0.8s linear infinite' }} /> Sending…</> : <>{info.channel === 'sms' ? <MessageCircle size={13} /> : <Mail size={13} />} Send test</>}
          </button>
        </>
      )}
    >
      <div style={{ fontSize: 12.5, color: 'var(--ci-txt2)', marginBottom: 10 }}>
        Sends this template once, through the real {info.channel === 'sms' ? 'SMS' : 'email'} pipeline, to a single guest you choose. Honors your outbound send-mode (hold / live).
      </div>
      <div className="ci-lab" style={{ marginTop: 0 }}>Recipient {info.channel === 'sms' ? '(guests with a phone)' : '(guests with an email)'}</div>
      <input className="ci-inp" value={q} onChange={(e) => { setQ(e.target.value); setChosen(null); }} placeholder="Search by name, email or phone…" />

      {guests === null && <div style={{ marginTop: 10 }}><Loading label="Loading guests…" /></div>}

      {guests !== null && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {matches.length === 0 && <div className="ci-empty">No matching guests.</div>}
          {matches.map((g) => {
            const sel = chosen && chosen.id === g.id;
            const label = `${g.first_name || ''} ${g.last_name || ''}`.trim() || (info.channel === 'sms' ? g.phone : g.email);
            return (
              <button
                key={g.id}
                className={`ci-btn ci-sm ${sel ? 'ci-gold' : ''}`}
                style={{ justifyContent: 'space-between', width: '100%' }}
                onClick={() => setChosen(g)}
              >
                <span>{label}</span>
                <span style={{ opacity: 0.7, fontSize: 11 }}>{info.channel === 'sms' ? g.phone : g.email}</span>
              </button>
            );
          })}
        </div>
      )}

      {error && <div className="ci-empty" style={{ color: '#e9897f', marginTop: 10 }}>{error}</div>}
    </Modal>
  );
}
